/**
 * TokenRevealDialog Component (Story 14.2)
 *
 * Shows the freshly minted portal URL ONCE after create/regenerate. The user
 * must explicitly acknowledge they've copied it before the dialog closes.
 * After dismissal, the raw token is unrecoverable from the UI.
 */

import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@provenleap/ui";
import { Copy, Warning, CheckCircle } from "@phosphor-icons/react";
import type { PortalLinkWithToken } from "../../lib/portalLinksApi";
import type { ToastType } from "../../hooks/useToast";

interface TokenRevealDialogProps {
  open: boolean;
  title: string;
  link: PortalLinkWithToken;
  onClose: () => void;
  showToast: (type: ToastType, message: string) => void;
}

export function TokenRevealDialog({
  open,
  title,
  link,
  onClose,
  showToast,
}: TokenRevealDialogProps) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      showToast("success", "URL copied to clipboard");
    } catch {
      showToast("error", "Could not copy — please copy manually");
    }
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                className="fixed inset-0 bg-black/40 z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-background p-8 shadow-lg"
                style={{ border: "0.5px solid var(--hairline)" }}
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <DialogPrimitive.Title className="text-lg font-semibold flex items-center gap-2">
                  <CheckCircle
                    size={20}
                    weight="fill"
                    className="text-green-600"
                  />
                  {title}
                </DialogPrimitive.Title>
                <div
                  className="mt-4 rounded-2xl bg-amber-50 p-4 text-[13px] text-amber-900 flex gap-3"
                  style={{ border: "0.5px solid rgb(252 211 77)" }}
                  role="alert"
                >
                  <Warning
                    size={18}
                    weight="bold"
                    className="shrink-0 mt-0.5 text-amber-700"
                  />
                  <p>
                    <strong>This is the only time you'll see this URL.</strong>{" "}
                    Copy it now — for security, the full URL cannot be retrieved
                    later. You can always regenerate a new URL if needed.
                  </p>
                </div>

                <div className="mt-4">
                  <label
                    htmlFor="portal-url-display"
                    className="text-[13px] font-medium block mb-2"
                  >
                    Share URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="portal-url-display"
                      type="text"
                      readOnly
                      value={link.url}
                      onFocus={(e) => e.currentTarget.select()}
                      className="flex-1 px-3 py-2 rounded-2xl text-[13px] font-mono bg-muted/40 shadow-xs"
                      style={{ border: "0.5px solid var(--hairline)" }}
                      data-testid="portal-url-input"
                    />
                    <Button
                      variant="brand"
                      className="rounded-2xl text-[13px] cursor-pointer"
                      onClick={handleCopy}
                      data-testid="copy-portal-url"
                    >
                      <Copy size={14} className="mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant={copied ? "brand" : "outline"}
                    className="rounded-2xl text-[13px] cursor-pointer"
                    style={
                      !copied ? { border: "0.5px solid var(--hairline)" } : undefined
                    }
                    onClick={onClose}
                    data-testid="close-token-reveal"
                  >
                    {copied ? "I've copied it — close" : "Dismiss"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
